import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["action", "disabled", "disabledDays", "error", "format", "id", "inline", "success", "label", "labelHidden", "mask", "message", "name", "onBlur", "onChange", "onFocus", "onMouseDown", "optionalLabel", "readOnly", "showOnFocus", "type", "value", "placement", "usePortal"],
    _excluded2 = ["modifiers"];
import '../../css-tokens';
/* eslint-disable react/forbid-prop-types */

import React, { forwardRef, useEffect, useRef } from 'react';
import cx from 'classnames';
import dayjs from 'dayjs';
import { usePopper as useReactPopper } from 'react-popper';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TextField from '../TextField'; // eslint-disable-next-line import/no-unresolved,import/extensions

import { ModifiersUtils } from '../../forks/react-day-picker';
import { mergeRefs } from '../../utils';
import system from '../../system';
import CalendarDialog from './CalendarDialog';
import IconLeft from './IconLeft';
import styles from './index.module.css';
import { getLocale } from '../../locale';
var SYSTEM_PROPS = ['marginProps']; // Don't extend on every render

var initializeDayJS = function () {
  var hasInitialized = false;
  return function () {
    if (hasInitialized) {
      return;
    }

    hasInitialized = true;
    dayjs.extend(customParseFormat);
    dayjs.locale(getLocale().name);
  };
}();

var formatInput = function formatInput(value, format) {
  return value ? dayjs(value).format(format) : '';
};

var isValidDate = function isValidDate(date, format) {
  return dayjs(date, format).format(format) === date;
};

const DatePicker = /*#__PURE__*/function () {
  const DatePicker = /*#__PURE__*/forwardRef(function (_ref, ref) {
    var _cx;

    var action = _ref.action,
        disabled = _ref.disabled,
        disabledDays = _ref.disabledDays,
        error = _ref.error,
        format = _ref.format,
        id = _ref.id,
        inline = _ref.inline,
        success = _ref.success,
        label = _ref.label,
        labelHidden = _ref.labelHidden,
        mask = _ref.mask,
        message = _ref.message,
        name = _ref.name,
        onBlur = _ref.onBlur,
        onChange = _ref.onChange,
        onFocus = _ref.onFocus,
        onMouseDown = _ref.onMouseDown,
        optionalLabel = _ref.optionalLabel,
        readOnly = _ref.readOnly,
        showOnFocus = _ref.showOnFocus,
        type = _ref.type,
        value = _ref.value,
        placement = _ref.placement,
        usePortal = _ref.usePortal,
        props = _objectWithoutPropertiesLoose(_ref, _excluded);

    React.useState(initializeDayJS);

    var _React$useState = React.useState(inline),
        show = _React$useState[0],
        setShow = _React$useState[1];

    var _React$useState2 = React.useState(undefined),
        month = _React$useState2[0],
        setMonth = _React$useState2[1];

    var _React$useState3 = React.useState(formatInput(value, format)),
        inputValue = _React$useState3[0],
        setInputValue = _React$useState3[1];

    var _React$useState4 = React.useState(null),
        referenceElement = _React$useState4[0],
        setReferenceElement = _React$useState4[1];

    var _React$useState5 = React.useState(null),
        popperElement = _React$useState5[0],
        setPopperElement = _React$useState5[1];

    var popper = useReactPopper(referenceElement, popperElement, {
      placement: placement
    });
    var focused = useRef(false); // Update input value & month when value changes

    useEffect(function () {
      if (value) {
        // Format the date to a valid input value
        var formattedInputValue = formatInput(value, format); // Update the internal input value

        if (formattedInputValue !== inputValue) {
          setInputValue(formattedInputValue);
        } // Set the month to the current calendar value


        if (month !== value) {
          setMonth(value);
        }
      } else {
        // Unset the month
        setMonth(undefined);
      }
    }, [value]);

    var handleShow = function handleShow() {
      return setShow(true);
    };

    var handleClose = function handleClose() {
      return setShow(false);
    };
    /**
     * Get the modifiers of the calendar
     * e.g. modifier.disabled, modifier.selected ...
     *
     * @param {string} calendarValue
     */


    var getModifiersForCalendarValue = function getModifiersForCalendarValue(calendarValue) {
      return (// Get the modifiers active in the datepicker
        ModifiersUtils.getModifiersForDay(calendarValue, _extends({
          disabled: disabledDays
        }, props.modifiers)).reduce(function (accumulator, currentValue) {
          var _extends2;

          return _extends({}, accumulator, (_extends2 = {}, _extends2[currentValue] = true, _extends2));
        }, {})
      );
    };
    /** Set the month to the active calendarValue */


    var handleMonthChange = function handleMonthChange() {
      return setMonth(value);
    };
    /**
     * Fires when the date in the calendar changes
     *
     * @param {Date} calendarValue
     * @param {Object} calendarModifiers
     */


    var handleCalendarChange = function handleCalendarChange(calendarValue, calendarModifiers) {
      // The selected day is disabled
      if (calendarModifiers.disabled) {
        return;
      } // Fire the onChange event with the CalendarValue, InputDate, Options provided by the Calendar


      onChange(calendarValue, _extends({
        formattedDate: formatInput(calendarValue, format),
        formattedDateIsValid: true,
        disabled: !!calendarModifiers.disabled
      }, calendarModifiers));
      handleClose();
    };
    /**
     * Fires when the TextField value change
     *
     * @namespace root.events.onchange
     */


    var handleInputChange = function handleInputChange(_ref2) {
      var target = _ref2.target;
      var newInputValue = target.value; // The value in the calendar

      var calendarValue = dayjs(newInputValue, format).toDate(); // Is the current day a valid date in the active format

      var isValidInputValue = isValidDate(newInputValue, format); // Get the modifiers active in the datepicker

      var calendarModifiers = getModifiersForCalendarValue(calendarValue); // We must update the input value for the inputMask

      if (newInputValue !== inputValue) {
        setInputValue(newInputValue);
      }

      var options = _extends({
        formattedDate: newInputValue,
        formattedDateIsValid: isValidInputValue,
        disabled: !!calendarModifiers.disabled
      }, calendarModifiers); // Fire onChange with both values


      if (isValidInputValue) {
        // Fire the onChange event with the CalendarValue, InputDate, Options provided by the Calendar
        onChange(calendarValue, options);
      } else {
        // Fire onChange with false as calendar value, so the user knows it's a disabled date
        onChange(false, options);
      }
    };

    var handleFocus = function handleFocus() {
      if (showOnFocus && !focused.current) {
        handleShow();
        focused.current = true;
      }

      onFocus();
    };

    var handleDialogClose = function handleDialogClose() {
      handleClose();
      focused.current = true;
      setTimeout(function () {
        focused.current = false;
      }, 2);
    };

    var _system = system(props, SYSTEM_PROPS),
        className = _system[0],
        style = _system[1],
        rest = _system[2]; // We don't want to have modifiers in our propTypes


    var modifiers = rest.modifiers,
        otherInputProps = _objectWithoutPropertiesLoose(rest, _excluded2);

    return /*#__PURE__*/React.createElement("div", {
      className: cx((_cx = {}, _cx[styles.root] = true, _cx[className] = !!className, _cx)),
      style: style
    }, /*#__PURE__*/React.createElement(TextField, _extends({}, otherInputProps, {
      success: success,
      mask: mask,
      action: action,
      disabled: disabled,
      error: error,
      id: id,
      label: label,
      labelHidden: labelHidden,
      message: message,
      name: name,
      onBlur: onBlur,
      onChange: handleInputChange,
      onFocus: handleFocus,
      onMouseDown: onMouseDown,
      optionalLabel: optionalLabel,
      readOnly: readOnly,
      ref: mergeRefs([setReferenceElement, ref]),
      type: type,
      value: inputValue,
      iconLeftComponent: /*#__PURE__*/React.createElement(IconLeft, {
        inline: inline,
        onClick: handleShow,
        disabled: disabled,
        accessibilityLabel: "Choose Date" + (inputValue ? ", Selected Date: " + inputValue : '')
      })
    })), show || inline ? /*#__PURE__*/React.createElement(CalendarDialog, {
      inline: inline,
      onClose: handleDialogClose,
      onCalendarChange: handleCalendarChange,
      disabled: disabled,
      month: month,
      disabledDays: disabledDays,
      onMonthChange: handleMonthChange,
      value: value,
      onChange: handleCalendarChange,
      modifiers: modifiers,
      setPopperElement: setPopperElement,
      popper: popper
    }) : null);
  });
  DatePicker.displayName = 'DatePicker';
  DatePicker.identifier = DatePicker.displayName; // used in docs website

  DatePicker.clonableProps = SYSTEM_PROPS;
  DatePicker.defaultProps = {
    format: 'DD/MM/YYYY',
    mask: '99/99/9999',
    placement: 'bottom-start',
    usePortal: false,
    showOnFocus: false,
    onFocus: () => {}
  };
  return DatePicker;
}();

export default DatePicker;